import { useEffect, useRef } from 'react';
import { useBuilderStore } from '../state/builderStore';

/** Gap (px) kept between the revealed row and the rail viewport edge it was scrolled past. */
const RAIL_REVEAL_GAP = 12;
/** How many frames we wait for the row to exist (a section auto-expands its block list on selection). */
const MAX_FRAMES = 4;

/**
 * Scroll the LEFT TREE so the current selection's row is visible — the mirror of
 * {@link import('../canvas/useRevealSelection').useRevealSelection}, which scrolls the canvas when you pick
 * in the tree. Without it, clicking a block IN THE CANVAS selected the row and opened its inspector, but on
 * a long tree (a product page with dozens of blocks) the highlighted row stayed off-screen, so you could not
 * tell what was selected (founder 2026-08-01).
 *
 * Why key on the selected ids and NOT on a nonce (the canvas reveal's approach): scrolling a row that is
 * ALREADY visible is a no-op here, so the rail path needs no source tracking — a click in the tree scrolls
 * nothing (the row you just clicked is on screen), a click in the canvas scrolls only when needed.
 *
 * Container-scoped `scrollBy` on `.bld-rail__list` (the rail's `overflow:auto` column), never
 * `Element.scrollIntoView`, so the editor document itself never moves and we control the exact edge gap.
 */
export function useRevealRailRow(): void {
  const selectedSectionId = useBuilderStore((s) => s.selectedSectionId);
  const selectedBlockId = useBuilderStore((s) => s.selectedBlockId);
  const firstRun = useRef(true);

  useEffect(() => {
    // The row may not exist yet: selecting a section auto-expands its block list, and selecting a block in
    // the canvas expands the owning section — both land a frame later. Retry for a few frames, then give up
    // (the rail can also be collapsed, or showing the Theme/Apps tab, in which case there is nothing to do).
    let frame = 0;
    let raf = 0;
    // A restored selection on mount is placed without animation; later reveals animate.
    const instant = firstRun.current;
    firstRun.current = false;

    const tick = () => {
      const el =
        (selectedBlockId &&
          document.querySelector<HTMLElement>(
            `[data-rail-block-id="${cssEscape(selectedBlockId)}"]`,
          )) ||
        (selectedSectionId &&
          document.querySelector<HTMLElement>(
            `[data-rail-section-id="${cssEscape(selectedSectionId)}"]`,
          )) ||
        null;

      if (!el) {
        if (++frame < MAX_FRAMES) raf = requestAnimationFrame(tick);
        return;
      }

      const container = el.closest<HTMLElement>('.bld-rail__list');
      if (!container) return;

      const cRect = container.getBoundingClientRect();
      const eRect = el.getBoundingClientRect();
      // Minimal move: only pull the row back inside the visible band, from whichever edge it fell past.
      let delta = 0;
      if (eRect.top < cRect.top + RAIL_REVEAL_GAP) delta = eRect.top - cRect.top - RAIL_REVEAL_GAP;
      else if (eRect.bottom > cRect.bottom - RAIL_REVEAL_GAP)
        delta = eRect.bottom - cRect.bottom + RAIL_REVEAL_GAP;
      if (Math.abs(delta) < 2) return; // already in view → no flicker

      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
      container.scrollBy({ top: delta, behavior: instant || reduceMotion ? 'auto' : 'smooth' });
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [selectedSectionId, selectedBlockId]);
}

/** Defensive attribute-selector escaping — nanoid/section ids are CSS-safe, but never trust raw input. */
function cssEscape(value: string): string {
  return typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(value) : value;
}
